/**
 * Accessors for vehicles stored with raw CheckCarDetails API payloads.
 * Shape: { uk_vehicle_data, vehicle_image_data, price, tags, ... }
 */

import { resolveApiAssetUrl } from "../shared/lib/createApiClient"
import { formatApiDate, normalizeFuelType, normalizeText } from "./textNormalize"

export const getVehicleRegistration = (vehicle) =>
    vehicle?.uk_vehicle_data?.VehicleRegistration || null

export const getVehicleImagesData = (vehicle) =>
    vehicle?.vehicle_image_data?.VehicleImages || null

export const getVehicleImageUrls = (vehicle) => {
    const custom = (vehicle?.custom_images || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((item) => resolveApiAssetUrl(item?.url))
        .filter(Boolean)
    if (custom.length) return custom

    const list = getVehicleImagesData(vehicle)?.ImageDetailsList || []
    const urls = list.map((item) => resolveApiAssetUrl(item?.ImageUrl)).filter(Boolean)
    if (urls.length) return urls
    if (vehicle?.model_image) return [resolveApiAssetUrl(vehicle.model_image)]
    return []
}

export const getVehicleCoverUrl = (vehicle) => getVehicleImageUrls(vehicle)[0] || null

export const getVehicleDisplayName = (vehicle) => {
    const reg = getVehicleRegistration(vehicle)
    if (reg?.MakeModel) return normalizeText(reg.MakeModel)
    if (reg?.Make) {
        return [normalizeText(reg.Make), normalizeText(reg.Model)].filter(Boolean).join(" ")
    }
    return normalizeText(vehicle?.make_model || vehicle?.make || "Vehicle")
}

export const getVehicleMake = (vehicle) =>
    normalizeText(getVehicleRegistration(vehicle)?.Make || vehicle?.make || "")

export const getVehicleModel = (vehicle) => {
    const reg = getVehicleRegistration(vehicle)
    if (reg?.Model) return normalizeText(reg.Model)
    const makeModel = reg?.MakeModel || vehicle?.make_model || ""
    const make = getVehicleMake(vehicle)
    if (make && makeModel.toUpperCase().startsWith(make.toUpperCase())) {
        return normalizeText(makeModel.slice(make.length).trim())
    }
    return normalizeText(makeModel)
}

export const getVehicleFuel = (vehicle) =>
    normalizeFuelType(
        getVehicleRegistration(vehicle)?.FuelType || vehicle?.fuel_type || ""
    )

export const getVehicleTransmission = (vehicle) =>
    normalizeText(
        getVehicleRegistration(vehicle)?.TransmissionType || vehicle?.transmission_type || ""
    )

export const getVehicleColour = (vehicle) =>
    normalizeText(getVehicleRegistration(vehicle)?.Colour || vehicle?.color || "")

export const getVehicleSeats = (vehicle) =>
    getVehicleRegistration(vehicle)?.SeatingCapacity || vehicle?.seating_capacity || ""

export const getVehicleBodyType = (vehicle) =>
    normalizeText(
        getVehicleRegistration(vehicle)?.DoorPlanLiteral || vehicle?.body_type || ""
    )

export const getVehicleVin = (vehicle) =>
    getVehicleRegistration(vehicle)?.Vin || vehicle?.vin || ""

export const getVehicleRegistrationDate = (vehicle) => {
    const reg = getVehicleRegistration(vehicle)
    const raw = reg?.DateFirstRegisteredUk || vehicle?.date_first_registered
    return raw ? formatApiDate(raw) : ""
}

export const getVehicleYear = (vehicle) => {
    const reg = getVehicleRegistration(vehicle)
    const registered = reg?.DateFirstRegisteredUk || vehicle?.date_first_registered
    if (!registered) return null
    const year = String(registered).slice(0, 4)
    return /^\d{4}$/.test(year) ? year : null
}

export const getVehicleYearPlate = (vehicle) => {
    const year = getVehicleYear(vehicle)
    return year ? `${String(year).slice(-2)} Plate` : null
}

export const getVehicleEngineCapacity = (vehicle) =>
    getVehicleRegistration(vehicle)?.EngineCapacity || vehicle?.engine_capacity || null

export const getVehicleTags = (vehicle) => {
    if (Array.isArray(vehicle?.tags)) return vehicle.tags
    return []
}

export const getVehicleLicensingAuthority = (vehicle) =>
    normalizeText(vehicle?.phv_licensing_authority || "")

const toNumberOrNull = (value) => {
    if (value === "" || value == null) return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

/**
 * Resolve optional with/without-insurance weekly packages.
 * Falls back to legacy `price` + `insurance_included` + `hire.deposit`.
 */
export const getHirePackages = (vehicle = {}) => {
    let withWeekly = toNumberOrNull(vehicle.price_with_insurance)
    let withoutWeekly = toNumberOrNull(vehicle.price_without_insurance)
    let withDeposit = toNumberOrNull(vehicle.hire?.deposit_with_insurance)
    let withoutDeposit = toNumberOrNull(vehicle.hire?.deposit_without_insurance)

    const legacyPrice = toNumberOrNull(vehicle.price)
    const legacyDeposit = toNumberOrNull(vehicle.hire?.deposit)

    if (withWeekly == null && withoutWeekly == null && legacyPrice != null) {
        if (vehicle.insurance_included === false || vehicle.insuranceIncluded === false) {
            withoutWeekly = legacyPrice
            if (withoutDeposit == null) withoutDeposit = legacyDeposit
        } else {
            withWeekly = legacyPrice
            if (withDeposit == null) withDeposit = legacyDeposit
        }
    }

    return {
        withInsurance:
            withWeekly != null
                ? {
                      weekly: withWeekly,
                      deposit: withDeposit != null ? withDeposit : 250,
                  }
                : null,
        withoutInsurance:
            withoutWeekly != null
                ? {
                      weekly: withoutWeekly,
                      deposit: withoutDeposit != null ? withoutDeposit : 200,
                  }
                : null,
    }
}

/** true = only with insurance, false = only without, null = both or neither */
export const getInsuranceIncluded = (vehicle) => {
    const { withInsurance, withoutInsurance } = getHirePackages(vehicle)
    if (withInsurance && !withoutInsurance) return true
    if (!withInsurance && withoutInsurance) return false
    if (typeof vehicle?.insurance_included === "boolean" && !withInsurance && !withoutInsurance) {
        return vehicle.insurance_included
    }
    if (typeof vehicle?.insuranceIncluded === "boolean" && !withInsurance && !withoutInsurance) {
        return vehicle.insuranceIncluded
    }
    return null
}

/** Lowest weekly price across available packages (0 if none). */
export const getVehiclePrice = (vehicle) => {
    const { withInsurance, withoutInsurance } = getHirePackages(vehicle)
    const prices = [withInsurance?.weekly, withoutInsurance?.weekly].filter((n) => n != null)
    if (prices.length) return Math.min(...prices)
    return Number(vehicle?.price) || 0
}

export const vehicleOffersInsurancePackage = (vehicle) =>
    Boolean(getHirePackages(vehicle).withInsurance)

export const vehicleOffersUninsuredPackage = (vehicle) =>
    Boolean(getHirePackages(vehicle).withoutInsurance)

export const formatVehicleWeeklyPriceLabel = (vehicle) => {
    const { withInsurance, withoutInsurance } = getHirePackages(vehicle)
    if (withInsurance && withoutInsurance) {
        if (withInsurance.weekly === withoutInsurance.weekly) {
            return `£${withInsurance.weekly.toFixed(0)}`
        }
        const from = Math.min(withInsurance.weekly, withoutInsurance.weekly)
        return `from £${from.toFixed(0)}`
    }
    if (withInsurance) return `£${withInsurance.weekly.toFixed(0)}`
    if (withoutInsurance) return `£${withoutInsurance.weekly.toFixed(0)}`
    return null
}

export const getVehicleAvailability = (vehicle) =>
    typeof vehicle?.available === "number" ? vehicle.available : vehicle?.driver ? 2 : 1

export const getVehicleMotData = (vehicle) =>
    vehicle?.uk_vehicle_data?.MotHistory || vehicle?.uk_vehicle_data?.mot || null

export const getVehicleTaxData = (vehicle) =>
    vehicle?.uk_vehicle_data?.VehicleTax || vehicle?.uk_vehicle_data?.tax || null

export const getVehicleSpecData = (vehicle) =>
    vehicle?.uk_vehicle_data?.VehicleSpec || vehicle?.uk_vehicle_data?.SmmtDetails || null
